import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ChevronLeft, ChevronRight, Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, formatMoney, formatDate } from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import { EntityLink } from "@/components/shared/EntityLink";

const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";
const fmt = (v: number | null | undefined, cur?: string) => formatMoney(v, cur);

function SimplePagination({
  page,
  setPage,
  totalPages,
  isFetching,
}: {
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  isFetching: boolean;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 mt-4 text-xs">
      <span className="text-slate-500 mr-2">
        Trang {page} / {totalPages}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="w-7 h-7"
        disabled={page <= 1 || isFetching}
        onClick={() => setPage(page - 1)}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="w-7 h-7"
        disabled={page >= totalPages || isFetching}
        onClick={() => setPage(page + 1)}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
      {isFetching && (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-2" />
      )}
    </div>
  );
}

// ── DELIVERIES TAB ──
export function OrderDeliveriesTab({
  orderId,
  customerId,
  customerName,
}: {
  orderId: string;
  customerId: string;
  customerName: string;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["order-deliveries-paginated", orderId, page],
    queryFn: async () =>
      trpcClient.deliveries.list.query({ orderId, page, limit } as any),
    placeholderData: (prev) => prev,
  });

  const deliveriesList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !deliveriesList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end p-2 border-b border-slate-50 bg-slate-50/50">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={() =>
            navigate({
              to: "/delivery/create",
              search: { orderId, customerId, customerName } as any,
            })
          }
        >
          <Plus className="h-3.5 w-3.5" /> Tạo phiếu giao hàng
        </Button>
      </div>
      {deliveriesList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có phiếu giao hàng
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Mã PGH</TableHead>
                <TableHead className="whitespace-nowrap">Ngày giao</TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  Số lượng
                </TableHead>
                <TableHead className="whitespace-nowrap">Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveriesList.map((d: any) => {
                const dCfg = getStatusConfig("DeliveryStatus", d.status);
                return (
                  <TableRow
                    key={d.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/delivery/$deliveryId",
                        params: { deliveryId: d.id },
                      } as any)
                    }
                  >
                    <TableCell className="font-mono text-xs font-medium">
                      <EntityLink
                        entityType="delivery"
                        id={d.id}
                        code={d.deliveryId || d.id}
                        search={{ returnUrl: `/orders/${orderId}?tab=deliveries` }}
                      />
                    </TableCell>
                    <TableCell>{fmtD(d.deliveryDate)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(
                        d.deliveryItems?.reduce(
                          (s: number, di: any) =>
                            s + (Number(di.deliveredQty || di.quantity) || 0),
                          0,
                        ) || 0,
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={dCfg.color}>
                        {dCfg.label || d.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── PRODUCTION ORDERS TAB ──
export function OrderProductionOrdersTab({
  orderId,
  onOpenCreatePO,
}: {
  orderId: string;
  onOpenCreatePO: () => void;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["order-production-orders-paginated", orderId, page],
    queryFn: async () =>
      trpcClient.productionOrders.list.query({
        salesOrderId: orderId,
        page,
        limit,
      } as any),
    placeholderData: (prev) => prev,
  });

  const poList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !poList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end p-2 border-b border-slate-50 bg-slate-50/50">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={onOpenCreatePO}
        >
          <Plus className="h-3.5 w-3.5" /> Tạo lệnh SX
        </Button>
      </div>
      {poList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có lệnh sản xuất
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Mã LSX</TableHead>
                <TableHead className="whitespace-nowrap">Ngày lập</TableHead>
                <TableHead className="whitespace-nowrap">Sản phẩm</TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  SL Kế hoạch
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  SL Đạt (KCS)
                </TableHead>
                <TableHead className="whitespace-nowrap">Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {poList.map((po: any) => {
                const poCfg = getStatusConfig(
                  "ProductionOrderStatus",
                  po.status,
                );
                return (
                  <TableRow
                    key={po.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      navigate({
                        to: "/production-orders/$orderId",
                        params: { orderId: po.id },
                      } as any)
                    }
                  >
                    <TableCell className="font-mono text-xs font-semibold text-indigo-700">
                      <EntityLink
                        entityType="productionOrder"
                        id={po.id}
                        code={po.orderId || po.id}
                        search={{ returnUrl: `/orders/${orderId}?tab=production_orders` }}
                      />
                    </TableCell>
                    <TableCell className="text-xs">{fmtD(po.createdAt)}</TableCell>
                    <TableCell className="text-xs font-medium">
                      {po.productId ? (
                        <EntityLink
                          entityType="product"
                          id={po.productId}
                          label={po.productName || "—"}
                          search={{ returnUrl: `/orders/${orderId}?tab=production_orders` }}
                        />
                      ) : (
                        po.productName || "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs font-mono font-medium">
                      {formatNumber(po.qtyPlan || po.qtyOrder)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs font-mono font-bold text-emerald-700">
                      {formatNumber(po.actualQuantity || 0)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={poCfg.color}>
                        {poCfg.label || po.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── PAYMENTS TAB ──
export function OrderPaymentsTab({ customerId }: { customerId: string }) {
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["order-receipts-paginated", customerId, page],
    queryFn: async () =>
      trpcClient.receipts.list.query({
        customerId,
        page,
        limit,
      }),
    placeholderData: (prev) => prev,
  });

  const receiptsList = (data as any)?.items || [];
  const total = (data as any)?.meta?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (isLoading && !receiptsList.length) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div>
      {receiptsList.length === 0 ? (
        <div className="text-center text-sm text-slate-600 py-6">
          Chưa có phiếu thanh toán
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Mã phiếu</TableHead>
                <TableHead className="whitespace-nowrap">Ngày</TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  Số tiền
                </TableHead>
                <TableHead className="whitespace-nowrap">Phương thức</TableHead>
                <TableHead className="whitespace-nowrap">Ghi chú</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receiptsList.map((r: any) => (
                <TableRow key={r.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs font-medium">
                    <EntityLink
                      entityType="receipt"
                      id={r.id}
                      code={r.receiptId || r.id}
                    />
                  </TableCell>
                  <TableCell>{fmtD(r.receiptDate)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-emerald-600">
                    {fmt(r.amount)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.paymentMethod === "CHUYEN_KHOAN"
                      ? "Chuyển khoản"
                      : r.paymentMethod === "TIEN_MAT"
                        ? "Tiền mặt"
                        : r.paymentMethod}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {r.notes || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <SimplePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isFetching={isFetching}
          />
        </div>
      )}
    </div>
  );
}

// ── PREPRESS TAB ──
export function OrderPrepressTab({
  order,
  onApproved,
}: {
  order: any;
  onApproved?: () => void;
}) {
  const [localDetails, setLocalDetails] = useState(
    (order.orderDetails || []).map((d: any) => ({
      ...d,
      prepressCode: d.prepressCode || d.prepressPrepressCode || "",
      prepressUps: d.prepressUps != null ? Number(d.prepressUps) : (d.prepressPrepresss != null ? Number(d.prepressPrepresss) : 1),
      prepressPaperSize: d.prepressPaperSize || "",
      prepressPrintSheets: d.prepressPrintSheets != null 
        ? Number(d.prepressPrintSheets) 
        : Math.ceil(Number(d.quantity || 1) / (Number(d.prepressUps || d.prepressPrepresss) || 1)),
      prepressNotes: d.prepressNotes || "",
    }))
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = (id: string, field: string, value: string | number) => {
    setLocalDetails((prev: any[]) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (field === "prepressUps" || field === "quantity") {
          const ups = Number(field === "prepressUps" ? value : item.prepressUps) || 1;
          const qty = Number(field === "quantity" ? value : item.quantity) || 1;
          updated.prepressPrintSheets = Math.ceil(qty / ups);
        }
        return updated;
      }),
    );
  };

  const handleApprove = async () => {
    try {
      setIsUpdating(true);

      // Validate prepress fields
      for (const item of localDetails) {
        if (!item.prepressCode || !item.prepressCode.trim()) {
          toast.error(`Vui lòng nhập Mã bài bình cho sản phẩm: ${item.productName}`);
          setIsUpdating(false);
          return;
        }
        if (!item.prepressPaperSize || !item.prepressPaperSize.trim()) {
          toast.error(`Vui lòng nhập Khổ giấy cho sản phẩm: ${item.productName}`);
          setIsUpdating(false);
          return;
        }
        if (!item.prepressUps || Number(item.prepressUps) < 1) {
          toast.error(`Số bát (ups) phải lớn hơn hoặc bằng 1 cho sản phẩm: ${item.productName}`);
          setIsUpdating(false);
          return;
        }
      }

      // Execute assignImposition for each detail item
      for (const item of localDetails) {
        if (item.lsxId || item.status === "PO_CREATED" || item.status === "IN_PRODUCTION") {
          continue;
        }

        const ups = Number(item.prepressUps) || 1;
        const printSheets = item.prepressPrintSheets 
          ? Number(item.prepressPrintSheets) 
          : Math.ceil(Number(item.quantity || 1) / ups);

        await trpcClient.orders.assignImposition.mutate({
          orderDetailId: item.id,
          prepressCode: item.prepressCode.trim(),
          prepressPaperSize: item.prepressPaperSize.trim(),
          prepressUps: ups,
          prepressPrintSheets: printSheets,
          prepressNotes: item.prepressNotes || null,
        });
      }

      toast.success("Đã duyệt bài bình thành công! Chi tiết đã sẵn sàng trong Bể chờ lập lệnh (Prepress Pool).");
      if (onApproved) {
        onApproved();
      }
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi duyệt bài bình");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-indigo-50/50 p-3 rounded-lg border border-indigo-100">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <span>Thông tin chế bản & Bình file (Prepress Imposition)</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Khai báo khổ giấy in, số bát (ups) và mã bài bình để đưa sản phẩm vào Bể chờ lập Lệnh sản xuất F0.
          </p>
        </div>
        <Button
          onClick={handleApprove}
          disabled={isUpdating}
          className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 font-medium text-xs h-8"
        >
          {isUpdating ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileText className="mr-1.5 h-3.5 w-3.5" />
          )}
          Duyệt bài bình
        </Button>
      </div>

      <div className="overflow-x-auto border rounded-lg border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="w-[120px]">Mã SP</TableHead>
              <TableHead className="min-w-[180px]">Tên SP</TableHead>
              <TableHead className="w-[100px] text-right">Số lượng (pcs)</TableHead>
              <TableHead className="w-[150px]">Khổ giấy / Khổ in</TableHead>
              <TableHead className="w-[90px] text-center">Số bát (Ups)</TableHead>
              <TableHead className="w-[110px] text-right">Số tờ in</TableHead>
              <TableHead className="w-[150px]">Mã bài bình (*)</TableHead>
              <TableHead className="w-[150px]">Ghi chú kỹ thuật</TableHead>
              <TableHead className="w-[120px] text-center">Trạng thái</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {localDetails.map((item: any) => {
              const isLocked = item.lsxId || item.status === "PO_CREATED" || item.status === "IN_PRODUCTION";
              return (
                <TableRow key={item.id} className="hover:bg-slate-50/80">
                  <TableCell className="font-mono text-xs text-slate-600">
                    {item.productId ? (
                      <EntityLink
                        entityType="product"
                        id={item.productId}
                        code={item.productCode || "—"}
                        search={{ returnUrl: `/orders/${order.id}?tab=prepress` }}
                      />
                    ) : (
                      item.productCode || "—"
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-xs text-slate-800">
                    {item.productId ? (
                      <EntityLink
                        entityType="product"
                        id={item.productId}
                        label={item.productName}
                        search={{ returnUrl: `/orders/${order.id}?tab=prepress` }}
                      />
                    ) : (
                      item.productName
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums font-semibold">
                    {formatNumber(item.quantity)}
                  </TableCell>
                  <TableCell>
                    <Input
                      placeholder="VD: 65x86"
                      value={item.prepressPaperSize || ""}
                      onChange={(e) =>
                        handleUpdate(item.id, "prepressPaperSize", e.target.value)
                      }
                      disabled={isLocked}
                      className="h-7 text-xs font-mono"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={1}
                      placeholder="VD: 4"
                      value={item.prepressUps || ""}
                      onChange={(e) =>
                        handleUpdate(item.id, "prepressUps", e.target.value)
                      }
                      disabled={isLocked}
                      className="h-7 text-xs text-center font-semibold"
                    />
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums font-mono text-indigo-700 font-semibold">
                    {formatNumber(item.prepressPrintSheets || Math.ceil(Number(item.quantity || 1) / (Number(item.prepressUps) || 1)))} tờ
                  </TableCell>
                  <TableCell>
                    <Input
                      placeholder="VD: BB-01"
                      value={item.prepressCode || ""}
                      onChange={(e) =>
                        handleUpdate(item.id, "prepressCode", e.target.value)
                      }
                      disabled={isLocked}
                      className="h-7 text-xs font-mono font-medium text-indigo-900 bg-indigo-50/30"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      placeholder="Ghi chú kỹ thuật..."
                      value={item.prepressNotes || ""}
                      onChange={(e) =>
                        handleUpdate(item.id, "prepressNotes", e.target.value)
                      }
                      disabled={isLocked}
                      className="h-7 text-xs"
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    {isLocked ? (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px]">
                        Đang sản xuất
                      </Badge>
                    ) : item.status === "PREPRESS_PLANNED" ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                        Đã bình file
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                        Chờ bình file
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
